/**
 * The builder's state, wired to React.
 *
 * A context and a reducer rather than a store library: the tree is small, the
 * whole surface is one screen, and `web_crm` does not carry zustand today. The
 * interesting logic all lives in `reducer.ts`, which is testable without a DOM;
 * this file only holds it, publishes the design upwards and owns the keyboard.
 *
 * `onChange` fires on every edit rather than on a save button. The builder sits
 * inside somebody else's form - the campaign editor, the template editor - and
 * that form owns saving. Two save buttons on one screen is how a letter gets
 * saved with a design that no longer matches its HTML.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import { builderReducer, findBlock, findParentColumn, type BuilderAction, type BuilderState } from "./reducer";
import { emptyDesign, type Block, type EmailDesign } from "./types";

interface BuilderValue extends BuilderState {
  dispatch: (a: BuilderAction) => void;
  selected: Block | null;
  /** True when the selected block sits inside a column - the inspector hides
   *  the controls that only make sense at the top level. */
  selectedInColumn: boolean;
  canUndo: boolean;
  canRedo: boolean;
  disabled: boolean;
}

const BuilderContext = createContext<BuilderValue | null>(null);

export function BuilderProvider({
  design,
  onChange,
  disabled = false,
  children,
}: {
  design: EmailDesign | null;
  onChange: (design: EmailDesign) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(builderReducer, undefined, () => ({
    design: design ?? emptyDesign(),
    selectedId: null,
    past: [],
    future: [],
  }));

  // Adopt a design handed in from outside - the campaign was loaded, or a
  // template was applied. Compared by identity on purpose: the parent holds the
  // design object in state and only replaces it when it means to. Comparing by
  // value would mean stringifying the whole letter on every keystroke.
  const adopted = useRef(design);
  useEffect(() => {
    if (design && design !== adopted.current) {
      adopted.current = design;
      dispatch({ kind: "load", design });
    }
  }, [design]);

  // Publish edits upward, but never the load that just came down - that would
  // mark the parent form dirty the moment the screen opens.
  const published = useRef(state.design);
  useEffect(() => {
    if (state.design === published.current) return;
    published.current = state.design;
    if (state.design !== adopted.current) {
      adopted.current = state.design;
      onChange(state.design);
    }
  }, [state.design, onChange]);

  const guarded = useCallback(
    (a: BuilderAction) => {
      // Read-only viewers may still click around; they just may not edit.
      if (disabled && a.kind !== "select") return;
      dispatch(a);
    },
    [disabled],
  );

  // Undo/redo. Skipped while a text field has focus, where the browser's own
  // undo is what the operator means.
  useEffect(() => {
    if (disabled) return;
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        dispatch({ kind: "undo" });
      } else if (e.key === "y" || (e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        dispatch({ kind: "redo" });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [disabled]);

  const value = useMemo<BuilderValue>(
    () => ({
      ...state,
      dispatch: guarded,
      selected: findBlock(state.design, state.selectedId),
      selectedInColumn: state.selectedId ? findParentColumn(state.design, state.selectedId) !== null : false,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      disabled,
    }),
    [state, guarded, disabled],
  );

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
}

export function useBuilder(): BuilderValue {
  const ctx = useContext(BuilderContext);
  if (!ctx) throw new Error("useBuilder must be used inside a BuilderProvider");
  return ctx;
}
